/* eslint-disable max-lines-per-function */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { Typography } from "@mui/material";
import logger from "services/logger";
import { useSnackbar } from "components/haven-design/snackbar";
import {
  getOrganizationsForManualContract,
  getPropertiesForManualContract,
  getSubscriptionsForManualContract,
  getSubscriptionsForManualContractVariables,
  getTeamForManualContract,
  getTeamForManualContractVariables,
  plansAll,
} from "types/gql-types";
import { EMPTY_ADDRESS } from "utils/address";
import { GET_PLANS_ALL } from "../plans/queries";
import ManualContractForm, { ManualContractFormValues } from "./form";
import {
  buildOrganizationOptions,
  buildPlanOptions,
  buildPropertyOptions,
  buildSignerOptions,
  buildSubscriptionOptions,
  toMutationInput,
} from "./contract-utils";
import {
  CREATE_MANUAL_PROPERTY_CONTRACT,
  GET_ORGANIZATIONS_FOR_MANUAL_CONTRACT,
  GET_PROPERTIES_FOR_MANUAL_CONTRACT,
  GET_SUBSCRIPTIONS_FOR_MANUAL_CONTRACT,
  GET_TEAM_FOR_MANUAL_CONTRACT,
} from "./queries";

export const EMPTY_MANUAL_CONTRACT_VALUES: ManualContractFormValues = {
  propertyId: "",
  organizationId: "",
  planId: "",
  signerUserId: "",
  unitCount: "",
  signedAt: null,
  billingFrequency: "",
  contractStartDate: null,
  initialTermEndDate: null,
  propertyLegalName: "",
  status: "signed",
  propertyPhone: "",
  propertyEmail: "",
  runPostSignSetup: false,
  signedPdfBase64: "",
  signedPdfFilename: "",
  propertyName: "",
  subscriptionId: "",
  ...EMPTY_ADDRESS,
};

const NewManualContract: React.FC = () => {
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbar();
  const [sending, setSending] = useState(false);
  const [organizationId, setOrganizationId] = useState("");
  const [selectedPropertyId, setSelectedPropertyId] = useState("");

  // errorPolicy "all" keeps the property list usable even when some properties
  // have incomplete (null) address fields, which the schema reports as
  // non-nullable field errors. Without this, Apollo discards the whole result
  // and the property dropdown renders empty ("No options").
  const { data: propertiesData } = useQuery<getPropertiesForManualContract>(
    GET_PROPERTIES_FOR_MANUAL_CONTRACT,
    { fetchPolicy: "network-only", errorPolicy: "all" },
  );
  const { data: plansData } = useQuery<plansAll>(GET_PLANS_ALL, { fetchPolicy: "no-cache" });
  const { data: organizationsData } = useQuery<getOrganizationsForManualContract>(
    GET_ORGANIZATIONS_FOR_MANUAL_CONTRACT,
    { fetchPolicy: "network-only" },
  );
  const { data: subscriptionsData } = useQuery<
  getSubscriptionsForManualContract,
  getSubscriptionsForManualContractVariables
  >(GET_SUBSCRIPTIONS_FOR_MANUAL_CONTRACT, {
    variables: { propertyId: selectedPropertyId },
    skip: !selectedPropertyId,
    fetchPolicy: "network-only",
  });
  const { data: teamData } = useQuery<
  getTeamForManualContract,
  getTeamForManualContractVariables
  >(GET_TEAM_FOR_MANUAL_CONTRACT, {
    variables: { id: organizationId },
    skip: !organizationId,
    fetchPolicy: "network-only",
  });

  // List is unmounted here; remount uses cache-and-network. Don't block the snackbar on refetch.
  const [createManualContract] = useMutation(CREATE_MANUAL_PROPERTY_CONTRACT);

  const submitForm = (values: ManualContractFormValues) => {
    setSending(true);
    return createManualContract({ variables: { input: toMutationInput(values) } })
      .then(() => {
        openSnackbar({ message: "Contract added", status: "success" });
        navigate("/dash/admin/contracts");
      })
      .catch((err) => {
        logger.sendError(err);
        openSnackbar({
          message: err?.graphQLErrors?.[0]?.message ?? err?.message ?? "Failed to add contract",
          status: "error",
        });
      })
      .finally(() => setSending(false));
  };

  return (
    <>
      <Typography variant="h5" gutterBottom>Add Contract</Typography>
      <ManualContractForm
        initialData={EMPTY_MANUAL_CONTRACT_VALUES}
        sending={sending}
        propertyOptions={buildPropertyOptions(propertiesData)}
        planOptions={buildPlanOptions(plansData)}
        signerOptions={buildSignerOptions(teamData)}
        organizationOptions={buildOrganizationOptions(organizationsData)}
        subscriptionOptions={buildSubscriptionOptions(subscriptionsData)}
        handleSubmit={submitForm}
        onPropertyChange={setOrganizationId}
        onPropertyIdChange={setSelectedPropertyId}
        onOrganizationChange={setOrganizationId}
      />
    </>
  );
};

export default NewManualContract;
